import { Autocomplete, Button, Grid, TextField, Typography } from "@mui/material";
import axios from "axios";
import React, { Fragment, memo, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { setSnackbar } from "../../../../../components/hooks/snackBar/reducers";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useStyle } from "../style";
import dayjs from "dayjs";
import _ from "lodash";
import CustomTable from "./customTable";
import moment from "moment";
import { Helmet } from "react-helmet-async";
import { CSVLink } from "react-csv";

const CustomerWalkInsReport = () => {
    const classes = useStyle();
    const dispatch = useDispatch();
    const pageOption = [10, 25, 50, 100];
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    const [pageCount, setPageCount] = useState(0);
    const [data, setData] = useState([]);
    const [supplier, setSupplier] = useState([]);
    const outletName = localStorage.getItem('outlet_name');
    const [dateLimit, setDateLimit] = useState({
        fromDate: moment().startOf('month').format(),
        toDate: moment().format()
    });
    const [options, setOptions] = useState({
        customer: { id: 0, fullname: "All" }
    });

    // Moved calculation here, based on filteredData
    const filteredData = useMemo(() => {
        if (!options.customer || options.customer.id === 0) return data;
        return data.filter(val => val.outletid === options.customer.id);
    }, [data, options.customer]);

    const rows = useMemo(() =>
        _.map(filteredData, (val, idx) => ({
            id: idx,
            sno: idx + 1,
            outletid: val.outletid,
            OutletCode: val.OutletCode,
            fullname: val.fullname,
            Youtube: val.Youtube,
            Facebook: val.Facebook,
            Instagram: val.Instagram,
            Others: val.Others,
            Regular: val.Regular,
            Walkin: val.Walkin
        })),
        [filteredData]
    );

    // Totals should now be for filteredData only
    const totals = useMemo(() => {
        if (filteredData.length) {
            return {
                Youtube: _.sumBy(filteredData, 'Youtube'),
                Facebook: _.sumBy(filteredData, 'Facebook'),
                Instagram: _.sumBy(filteredData, 'Instagram'),
                Others: _.sumBy(filteredData, 'Others'),
                Regular: _.sumBy(filteredData, 'Regular'),
                Walkin: _.sumBy(filteredData, 'Walkin'),
            };
        }
        return {
            Youtube: 0,
            Facebook: 0,
            Instagram: 0,
            Others: 0,
            Regular: 0,
            Walkin: 0,
        };
    }, [filteredData]);

    const columns = [
        { field: "sno", headerName: "S.no", minWidth: 70, maxWidth: 100, headerAlign: "center", align: "center", sortable: false },
        { field: "fullname", headerName: "Outlet Name", flex: 1, headerAlign: "center", align: "center", sortable: false },
        { field: "OutletCode", headerName: "Outlet Code", flex: 1, headerAlign: "center", align: "center", sortable: false },
        // { field: "outletid", headerName: "Outlet ID", flex: 1, headerAlign: "center", align: "center", sortable: false },
        { field: "Youtube", headerName: "Youtube", flex: 1, headerAlign: "center", align: "center", sortable: false },
        { field: "Facebook", headerName: "Facebook", flex: 1, headerAlign: "center", align: "center", sortable: false },
        { field: "Instagram", headerName: "Instagram", flex: 1, headerAlign: "center", align: "center", sortable: false },
        { field: "Others", headerName: "Others", flex: 1, headerAlign: "center", align: "center", sortable: false },
        { field: "Regular", headerName: "Regular", flex: 1, headerAlign: "center", align: "center", sortable: false },
        { field: "Walkin", headerName: "Walkin", flex: 1, headerAlign: "center", align: "center", sortable: false }
    ];

    const props = {
        columns,
        rows,
        page,
        setPage,
        rowsPerPage,
        setRowsPerPage,
        pageCount,
        pageOption,
    };

    // CSV data generator including totals row
    const generateCSVData = () => {
        const csvData = [
            [],
            // ["S.no", "Outlet Name", "Outlet Code", "Outlet ID", "Youtube", "Facebook", "Instagram", "Others", "Regular" , "Walkin"],
            ["S.no", "Outlet Name", "Outlet Code" , "Youtube", "Facebook", "Instagram", "Others", "Regular" , "Walkin"],
        ];
        rows.forEach(row => {
            csvData.push([
                row.sno,
                row.fullname,
                row.OutletCode,
                // row.outletid,
                row.Youtube,
                row.Facebook,
                row.Instagram,
                row.Others,
                row.Regular,
                row.Walkin,
            ]);
        });
        csvData.push([]);
        csvData.push([
            "",
            "Total",
            "",
            totals.Youtube,
            totals.Facebook,
            totals.Instagram,
            totals.Others,
            totals.Regular,
            totals.Walkin,
        ]);
        return csvData;
    };

    useEffect(() => {
        getSupplierData();
    }, []);

    useEffect(() => {
        if (dateLimit.fromDate !== null && dateLimit.toDate !== null) {
            handleOutletSalesItemWise();
        }
    }, [rowsPerPage, page, dateLimit, options]);

    function handleChange(e, name) {
        setDateLimit(prev => ({ ...prev, [name]: dayjs(e).format() }));
    }

    function getSupplierData() {
        axios
            .get("/v1/outlet/list/1")
            .then((res) => {
                setSupplier(res.data);
            })
            .catch((err) => {
                dispatch(setSnackbar(true, "error", err.response.data.error));
            });
    }

    function handleOutletSalesItemWise() {
        // Use required API contract: fromdate and todate (not from_date, to_date)
        const postData = {
            fromdate: moment(dateLimit.fromDate).format('YYYY-MM-DD'),
            todate: moment(dateLimit.toDate).format('YYYY-MM-DD'),
        };
        axios.post('/v1/reports/outlet_sales/customer/walkin/report', postData)
            .then(res => {
                setData(res.data);
                setPageCount(res.data.length);
            })
            .catch(err => {
                setData([]);
                dispatch(setSnackbar(true, "error", err.response.data.errors[0].message));
            });
    }

    function handleOptions(value, name) {
        setOptions((prev) => ({ ...prev, [name]: value }));
    }

    return (
        <Fragment>
            <Helmet><title>Customer Walk Ins Reports</title></Helmet>
            <Typography variant="h6">Customer Walk Ins Reports</Typography>
            <Grid container spacing={2} className="p-2 pt-2">
                <Grid item md={3}>
                    <Autocomplete
                        size="small"
                        options={[{ id: 0, fullname: "All" }, ...supplier]}
                        onChange={(e, value) => handleOptions(value, 'customer')}
                        getOptionLabel={option => option.fullname}
                        renderInput={(params) => <TextField {...params} label="Select Outlet" />}
                    />
                </Grid>
                <Grid item md={3}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            onChange={(e) => handleChange(e, "fromDate")}
                            value={dayjs(dateLimit.fromDate)}
                            format='DD-MM-YYYY'
                            slotProps={{
                                textField: {
                                    size: "small",
                                    label: 'From',
                                    style: { width: '100%' }
                                },
                            }}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item md={3}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            onChange={(e) => handleChange(e, "toDate")}
                            value={dayjs(dateLimit.toDate)}
                            format='DD-MM-YYYY'
                            slotProps={{
                                textField: {
                                    size: "small",
                                    label: 'To',
                                    style: { width: '100%' }
                                },
                            }}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item md={3} style={{ display: 'flex', alignItems: 'start', justifyContent: "end" }}>
                    <CSVLink data={generateCSVData()} filename={"Outlet_Customer_Walk_Ins_Reports.csv"}>
                        <Button variant="contained" color="primary">
                            Download CSV
                        </Button>
                    </CSVLink>
                </Grid>
            </Grid>
            <CustomTable props={props} totals={totals} />
        </Fragment>
    );
};

export default memo(CustomerWalkInsReport);
